import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

export type EntityType = 'contract' | 'meter' | 'opportunity' | 'order'

export interface ContextEntity {
  entity_id: string
  entity_schema: string
}

export interface UseContextEntitiesOptions {
  /** List of allowed schemas to extract from URL. If not provided, uses legacy hard-coded list. */
  allowedSchemas?: string[]
}

export interface ContextEntitiesResult {
  /** @deprecated Use contextEntities array instead */
  contractId: string | null
  /** @deprecated Use contextEntities array instead */
  meterId: string | null
  /** @deprecated Use contextEntities array instead */
  opportunityId: string | null
  /** @deprecated Use contextEntities array instead */
  orderId: string | null
  contextEntities: ContextEntity[]
}

/**
 * Universal hook for extracting entity IDs from URL search parameters and
 * building context entities array for API calls.
 *
 * @param options - Configuration options
 * @param options.allowedSchemas - List of allowed entity schemas to extract from URL parameters.
 *                                  If provided, will extract any `{schema}Id` parameter for schemas in this list.
 *                                  If not provided, uses legacy hard-coded list (contract, meter, opportunity, order).
 */
export const useContextEntities = (
  options?: UseContextEntitiesOptions
): ContextEntitiesResult => {
  const location = useLocation()
  const { allowedSchemas } = options || {}

  return useMemo(() => {
    const params = new URLSearchParams(location.search)
    const entities: ContextEntity[] = []

    // Determine which schemas to extract
    const schemasToExtract = allowedSchemas || [
      'contract',
      'meter',
      'opportunity',
      'order'
    ]

    // Extract entity IDs for all allowed schemas
    const extractedIds: Record<string, string | null> = {}

    schemasToExtract.forEach((schema) => {
      const paramName = `${schema}Id`
      const entityId = params.get(paramName)

      if (entityId) {
        extractedIds[paramName] = entityId
        entities.push({
          entity_id: entityId,
          entity_schema: schema
        })
      } else {
        extractedIds[paramName] = null
      }
    })

    // For backward compatibility, always return the legacy hard-coded properties
    return {
      contractId: extractedIds['contractId'] || null,
      meterId: extractedIds['meterId'] || null,
      opportunityId: extractedIds['opportunityId'] || null,
      orderId: extractedIds['orderId'] || null,
      contextEntities: entities
    }
  }, [location.search, allowedSchemas])
}
